import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Check,
  Loader2,
  Clock,
  Clapperboard,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { appApiBaseUrl, appFetch } from '@/shared/api/app-client'
import type { ApiResponse, ClippingJobResult, ClippingJobStatus } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'

/** 7 步进度定义 — 映射到后端 status */
const processingSteps = [
  { key: 'preparing', label: '准备媒体', statuses: ['transcribing'] },
  { key: 'analyzing', label: '分析视频', statuses: ['transcribing'] },
  { key: 'transcribing', label: '转录音频', statuses: ['transcribing'] },
  { key: 'finding_topics', label: '寻找主题', statuses: ['analyzing'] },
  { key: 'creating_clips', label: '创建片段', statuses: ['analyzing'] },
  { key: 'generating_captions', label: '生成字幕', statuses: ['analyzing'] },
  { key: 'finalizing', label: '最终化片段', statuses: ['analyzing'] },
]

/** 根据后端 status 计算当前步骤索引 */
function getStepIndex(status: ClippingJobStatus): number {
  if (status === 'transcribing') return 2  // 转录中 → 第 3 步活跃
  if (status === 'analyzing') return 4      // 分析中 → 第 5 步活跃
  if (status === 'completed') return 7      // 完成 → 全部完成
  if (status === 'failed') return 0         // 失败 → 停在第 1 步
  return 0
}

function resolveMediaUrl(src: string) {
  return src.startsWith('/uploads/') ? `${appApiBaseUrl}${src}` : src
}

export function ProcessingCanvas({
  taskId,
  videoTitle,
  videoSrc,
  style,
  language,
  orientation,
  resolution,
}: {
  taskId: string
  videoTitle?: string
  videoSrc?: string
  style?: string
  language?: string
  orientation?: string
  resolution?: string
}) {
  const navigate = useNavigate()
  const [job, setJob] = useState<ClippingJobResult | null>(null)
  const [currentStep, setCurrentStep] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!taskId) return

    let cancelled = false

    async function poll() {
      try {
        const res = await appFetch<ApiResponse<ClippingJobResult>>(
          `/api/clipping/jobs/${taskId}`,
        )
        if (!cancelled) {
          setJob(res.data)
          setCurrentStep(getStepIndex(res.data.status))

          // 完成 → 跳转片段结果页
          if (res.data.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current)
            // 延迟 2 秒让用户看到完成状态
            setTimeout(() => {
              navigate({
                to: '/app/clip-results',
                search: {
                  taskId,
                  title: videoTitle,
                  src: videoSrc,
                  style,
                  language,
                  orientation,
                  resolution,
                },
              })
            }, 2000)
          }

          // 失败 → 停止轮询
          if (res.data.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current)
          }
        }
      } catch {
        // 轮询失败时静默，下次重试
      }
    }

    // 立即查一次
    poll()

    // 每 5 秒轮询
    pollRef.current = setInterval(poll, 5000)

    return () => {
      cancelled = true
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [taskId, navigate])

  const isFailed = job?.status === 'failed'
  const isCompleted = job?.status === 'completed'

  return (
    <div className="min-h-screen bg-background">
      {/* 顶栏（无侧边栏，只有返回箭头） */}
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-5 backdrop-blur">
        <Link
          to="/app"
          className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <span className="text-sm font-semibold text-foreground">Video Clips</span>
      </div>

      {/* 视频信息栏 */}
      <div className="mx-auto mt-6 flex max-w-[724px] flex-wrap items-start justify-between gap-2.5 rounded-2xl border border-border bg-card px-3 py-3 shadow-md sm:gap-6 sm:px-5">
        <div className="flex w-full items-start gap-3 sm:max-w-[60%]">
          <div className="flex h-[65px] w-[107px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
            {videoSrc ? (
              <video
                src={resolveMediaUrl(videoSrc)}
                className="h-full w-full object-cover"
                muted
                playsInline
                preload="metadata"
                crossOrigin="anonymous"
                onLoadedData={(event) => {
                  try {
                    event.currentTarget.currentTime = 0.1
                  } catch {
                    // 浏览器不支持定位首帧时保留默认画面。
                  }
                }}
              />
            ) : <Clapperboard className="size-6 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="truncate text-sm font-medium text-foreground">
              {videoTitle ?? '正在处理'}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {isFailed ? '处理失败' : isCompleted ? '处理完成' : '分析中…'}
            </div>
          </div>
        </div>
      </div>

      {/* 分析进度区 */}
      <div className="mx-auto mt-8 max-w-[638px] px-5 pb-20">
        {/* 标题 */}
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-lg font-bold leading-[1.3] text-foreground">
            {isFailed ? '分析失败' : isCompleted ? '分析完成' : '正在分析视频'}
          </h1>
          {!isFailed && !isCompleted ? (
            <div className="flex flex-col items-end gap-1">
              <span className="text-sm font-medium text-muted-foreground">预计时间</span>
              <span className="text-sm font-semibold text-primary">约 2-5 分钟</span>
            </div>
          ) : null}
        </div>

        {/* 错误信息 */}
        {isFailed ? (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-medium text-destructive">处理失败</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {job?.error ?? '未知错误'}
              </p>
            </div>
          </div>
        ) : null}

        {/* 成功信息 */}
        {isCompleted ? (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <Check className="mt-0.5 size-5 shrink-0 text-emerald-500" />
            <div>
              <p className="text-sm font-medium text-emerald-600">
                分析完成，找到 {job?.clips?.length ?? 0} 个高光片段
              </p>
              <p className="mt-1 text-xs text-muted-foreground">即将跳转到项目列表…</p>
            </div>
          </div>
        ) : null}

        {/* 取消处理 */}
        {!isFailed && !isCompleted ? (
          <button
            type="button"
            onClick={() => navigate({ to: '/app' })}
            className="mt-2 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          >
            取消处理
          </button>
        ) : null}

        {/* 提示 */}
        {!isFailed && !isCompleted ? (
          <p className="mt-4 text-xs font-medium leading-normal text-muted-foreground">
            你可以离开此页面，分析完成后我们会通知你。
          </p>
        ) : null}

        {/* 进度步骤 */}
        {!isFailed ? (
          <div className="mt-4 space-y-3 rounded-xl border border-border bg-card p-4 sm:p-6">
            {processingSteps.map((step, index) => {
              const isDone = index < currentStep
              const isActive = index === currentStep && !isCompleted

              return (
                <div key={step.key} className="flex items-center gap-2.5">
                  <div className="flex w-5 shrink-0 justify-center">
                    {isDone || isCompleted ? (
                      <Check className="size-5 text-primary" />
                    ) : isActive ? (
                      <Loader2 className="size-5 animate-spin text-primary" />
                    ) : (
                      <div className="size-5 rounded-full border-2 border-muted" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-sm font-medium leading-normal transition-colors',
                      isDone || isCompleted
                        ? 'text-foreground'
                        : isActive
                          ? 'text-foreground'
                          : 'text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>
        ) : null}

        {/* 失败时显示重试按钮 */}
        {isFailed ? (
          <div className="mt-6 flex justify-center gap-3">
            <Button
              variant="outline"
              className="rounded-full px-6"
              onClick={() => navigate({ to: '/app' })}
            >
              返回首页
            </Button>
          </div>
        ) : null}

        {/* 正常时显示返回按钮 */}
        {!isFailed && !isCompleted ? (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              className="rounded-full px-6"
              onClick={() => navigate({ to: '/app' })}
            >
              <Clock className="size-4" />
              返回首页
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
